<script setup lang="ts">
import { computed } from 'vue'

const authStore = useAuthStore()
const { activePlayerId } = useTournamentHelpers()

const props = withDefaults(defineProps<{
  game: Game | ManageGame
  player: Player
  showParentGroup?: boolean
  large?: boolean
}>(), { showParentGroup: false, large: false })

const isPlayer1 = computed(() => {
  return getPlayer(props.game.player1).id === getPlayer(props.player).id
})

const isWinner = computed(() => {
  return getPlayer(props.player).is_winner
})

const isLoser = computed(() => {
  return props.game.concluded_at && !getPlayer(props.player).is_winner
})

const isActive = computed(() => {
  return activePlayerId.value === getPlayer(props.player).id
})

const isBullWinner = computed(() => {
  if (props.game.bull_winner) {
    return (props.game.bull_winner === 1 && getPlayer(props.player).id === getPlayer(props.game.player1).id) || (props.game.bull_winner === 2 && getPlayer(props.player).id === getPlayer(props.game.player2).id)
  }

  return false
})

const getAverage = computed(() => {
  if (isPlayer1.value) {
    return props.game.player_1_avg
  }

  return props.game.player_2_avg
})
</script>

<template>
  <div
    class="flex flex-row transition-all max-w-full"
    :class="{
      'bg-gray-200 dark:bg-gray-500 opacity-50': $getPlayer(player).id === 0,
      'dark:bg-gray-800 bg-gray-100': game.concluded_at === null,
      'active-player dark:!bg-gray-500 bg-gray-300': isActive && !isWinner && !isLoser,
      'is-winner dark:bg-green-800 bg-green-200': isWinner && !isActive,
      'is-winner active-player dark:bg-green-600 bg-green-500': isWinner && isActive,
      'dark:bg-red-500 bg-red-400': isLoser && isActive,
      'dark:bg-red-800 bg-red-200': isLoser && !isActive,
      'font-bold': $getPlayer(player).id === authStore.user?.player_id,
      'text-xs': !large,
    }"
    @mouseenter="activePlayerId = $getPlayer(player).id !== 0 ? $getPlayer(player).id : null"
    @mouseleave="activePlayerId = null"
  >
    <div class="p-0.5 pl-1.5 flex-1 !flex flex-row pr-2 truncate text-ellipsis overflow-hidden">
      <UiTooltip v-if="isBullWinner">
        <span
          class="mr-0.5"
        >
          🎯
        </span>
        <template #content>
          {{ $t('Bull winner') }}
        </template>
      </UiTooltip>
      <div class="flex-shrink overflow-hidden">
        <UiTooltip :delay-duration="1000">
          <UiTooltipTrigger as-child>
            <div class="overflow-hidden truncate">
              <template v-if="showParentGroup && $getPlayer(player).parent_group">
                {{ $getPlayer(player).parent_group }}-{{ $getPlayer(player).parent_group_position }} -
              </template>
              {{ $getPlayer(player).name }}
            </div>
          </UiTooltipTrigger>
          <UiTooltipContent>
            <template v-if="showParentGroup">
              (Group: {{ $getPlayer(player).parent_group }} - Position: {{ $getPlayer(player).parent_group_position }})
            </template>
            {{ $getPlayer(player).name }}
          </UiTooltipContent>
        </UiTooltip>
      </div>
      <div
        v-if="getAverage"
        class="ml-auto"
      >
        ({{ getAverage }})
      </div>
      <div
        v-else-if="game.concluded_at === null && game.started_at && game.recent_score"
        class="ml-auto"
      >
        <strong>{{ game.recent_score?.[isPlayer1 ? 'player1' : 'player2'].last_throw?.s }}</strong>
        ({{ game.recent_score?.[isPlayer1 ? 'player1' : 'player2'].remaining }})
      </div>
    </div>
    <div
      class="ml-auto dark:bg-primary bg-primary text-primary-foreground p-0.5 w-8 text-center"
      :class="{ 'font-bold': $getPlayer(player).is_winner }"
    >
      {{ $getPlayer(player).score }}
    </div>
  </div>
</template>
